import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "react-query";
import {
  getProductMonthWiseData,
  getProductDayWiseData,
  getAllProducts,
} from "../../services/analyticsService";
import { getGeneralInfo } from "../../services/generalInfoService";
import { formatAmount } from "../../utils/formatters";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { subMonths, startOfMonth, subDays } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import Pagination from "../../components/Pagination";

const getInitialStartDate = (timeRange) => {
  const today = new Date();
  if (timeRange === "month") {
    return startOfMonth(subMonths(today, 2)); // 3 months (current month + 2 previous months)
  } else {
    return subDays(today, 90); // 3 months (90 days) for day-wise
  }
};

const toLocalMidnight = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const ProductAnalytics = () => {
  const location = useLocation();
  const STORAGE_KEY = "filters:product-analytics";
  let savedFilters = null;
  try {
    savedFilters = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (_) { }
  const TODAY_KEY = new Date().toLocaleDateString("en-CA");
  if (savedFilters && savedFilters.dateKey !== TODAY_KEY) {
    savedFilters = null;
  }

  // Get URL params first
  const [searchParams] = useSearchParams();
  const stateParam = searchParams.get("state") || "";
  const platformParam = searchParams.get("platform") || "";
  const startDateParam = searchParams.get("startDate") || "";
  const endDateParam = searchParams.get("endDate") || "";

  // Check if this is a fresh navigation (from sidebar) - no URL params, no location.state
  const isFreshNavigation = !stateParam && !platformParam && !startDateParam && !endDateParam && !location.state?.state && !location.state?.postingAccount;

  // Initialize timeRange first so it can be used in date initialization (day-wise only, no hour)
  const rawTimeRange = location.state?.timeRange || savedFilters?.timeRange || "month";
  const initialTimeRange = rawTimeRange === "hour" ? "day" : rawTimeRange;
  
  // Get dates from navigation state or use defaults
  const getInitialStartDateFromState = (timeRangeValue) => {
    if (location.state?.startDate) {
      const date = new Date(location.state.startDate);
      return toLocalMidnight(date);
    }
    const initialDate = timeRangeValue === "month" ? getInitialStartDate("month") : getInitialStartDate("day");
    return toLocalMidnight(initialDate);
  };

  const getInitialEndDateFromState = (timeRangeValue) => {
    if (location.state?.endDate) {
      const date = new Date(location.state.endDate);
      return toLocalMidnight(date);
    }
    return toLocalMidnight(new Date());
  };

  const [startDate, setStartDate] = useState(() => {
    if (location.state?.startDate) {
      return toLocalMidnight(new Date(location.state.startDate));
    }
    if (startDateParam) {
      return toLocalMidnight(new Date(startDateParam));
    }
    if (savedFilters?.startDate && !isFreshNavigation) {
      return toLocalMidnight(new Date(savedFilters.startDate));
    }
    return getInitialStartDateFromState(initialTimeRange);
  });
  const [endDate, setEndDate] = useState(() => {
    if (location.state?.endDate) {
      return toLocalMidnight(new Date(location.state.endDate));
    }
    if (endDateParam) {
      return toLocalMidnight(new Date(endDateParam));
    }
    if (savedFilters?.endDate && !isFreshNavigation) {
      return toLocalMidnight(new Date(savedFilters.endDate));
    }
    return getInitialEndDateFromState(initialTimeRange);
  });
  const [postingAccount, setPostingAccount] = useState(
    location.state?.postingAccount || platformParam || (savedFilters?.postingAccount && !isFreshNavigation ? savedFilters.postingAccount : "")
  );
  const [state, setState] = useState(
    location.state?.state || stateParam || (savedFilters?.state && !isFreshNavigation ? savedFilters.state : "")
  );
  const [zone, setZone] = useState(
    location.state?.zone || (savedFilters?.zone && !isFreshNavigation ? savedFilters.zone : "")
  );

  // Fetch states for dropdown when zone is selected (same as Orders page)
  const { data: statesData } = useQuery(
    ["general-info", "states"],
    () => getGeneralInfo("states"),
    { enabled: !!zone, staleTime: 10 * 60 * 1000 }
  );
  const statesForZone = useMemo(() => {
    if (!zone || !Array.isArray(statesData)) return [];
    return statesData.filter(
      (item) => String(item?.zone || "").toLowerCase() === zone.toLowerCase()
    );
  }, [zone, statesData]);

  // Reset state when zone changes (same as Orders page)
  const prevZoneRef = useRef(zone);
  useEffect(() => {
    if (prevZoneRef.current !== zone) {
      prevZoneRef.current = zone;
      setState("");
    }
  }, [zone]);
  const [showQuantity, setShowQuantity] = useState(
    location.state?.showQuantity ?? savedFilters?.showQuantity ?? false
  );
  const [showAllProducts, setShowAllProducts] = useState(
    location.state?.showAllProducts ?? savedFilters?.showAllProducts ?? false
  );
  const [selectedProducts, setSelectedProducts] = useState(
    savedFilters?.selectedProducts || []
  );
  const [selectedProductSkus, setSelectedProductSkus] = useState({});
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [debouncedProductSearchTerm, setDebouncedProductSearchTerm] = useState("");
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const productDropdownRef = useRef(null);
  const [timeRange, setTimeRange] = useState(initialTimeRange);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"
  const [page, setPage] = useState(1);
  const [limit] = useState(50); // Items per page
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProductSearchTerm(productSearchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  // Clear filters when navigating directly from sidebar (no URL params, no location.state)
  useEffect(() => {
    // Check if this is a fresh navigation from sidebar
    const hasUrlParams = stateParam || platformParam || startDateParam || endDateParam;
    const hasLocationState = location.state?.state || location.state?.postingAccount || location.state?.startDate || location.state?.endDate;
    
    // If navigating to /analytics/product (not /analytics/product-by-state) with no params, clear state
    const isProductRoute = location.pathname === "/analytics/product";
    
    // If no URL params and no location.state and on product route, this is a fresh navigation - clear state filter
    if (isProductRoute && !hasUrlParams && !hasLocationState && state) {
      setState("");
      setPage(1); // Reset to first page
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Persist filters
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          dateKey: TODAY_KEY,
          startDate: startDate?.toISOString?.() || startDate,
          endDate: endDate?.toISOString?.() || endDate,
          postingAccount,
          state,
          zone,
          showQuantity,
          showAllProducts,
          timeRange,
          selectedProducts,
        })
      ); 
    } catch (_) { }
  }, [
    startDate,
    endDate,
    postingAccount,
    state,
    zone,
    showQuantity,
    showAllProducts,
    timeRange,
    selectedProducts,
  ]);

  // Track previous timeRange to detect when switching TO monthly
  const prevTimeRangeRef = React.useRef(timeRange);
  
  // Adjust dates when switching time ranges
  useEffect(() => {
    // Only adjust when switching TO a different time range
    if (prevTimeRangeRef.current === timeRange) {
      prevTimeRangeRef.current = timeRange;
      return;
    }

    // Clear selected products when time range changes
    setSelectedProducts([]);

    if (timeRange === "month" && endDate) {
      const endDateMonth = endDate.getMonth();
      const endDateYear = endDate.getFullYear();
      
      // Always set start date to 1st of the month when in monthly view
      const firstDayOfMonth = new Date(endDateYear, endDateMonth, 1);
      setStartDate(toLocalMidnight(firstDayOfMonth));
      
      // Set end date to last day of the month
      const lastDayOfMonth = new Date(endDateYear, endDateMonth + 1, 0);
      setEndDate(toLocalMidnight(lastDayOfMonth));
    }
    
    // Update the ref for next comparison
    prevTimeRangeRef.current = timeRange;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]); // Only run when timeRange changes

  const navigate = useNavigate();

  const handleStartDateChange = (date) => {
    if (timeRange === "month") {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      setStartDate(toLocalMidnight(firstDay));
    } else {
      setStartDate(toLocalMidnight(date));
    }
  };

  const handleEndDateChange = (date) => {
    if (timeRange === "month") {
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      setEndDate(toLocalMidnight(lastDay));
      const newStartDate = new Date(date.getFullYear(), date.getMonth() - 2, 1); // 3 months (current month + 2 previous months)
      if (toLocalMidnight(date) < startDate) {
        setStartDate(toLocalMidnight(newStartDate));
      }
    } else {
      setEndDate(toLocalMidnight(date));
      const newStartDate = new Date(date);
      newStartDate.setDate(newStartDate.getDate() - 90); // 3 months (90 days)
      if (toLocalMidnight(date) < startDate) {
        setStartDate(toLocalMidnight(newStartDate));
      }
    }
  };

  const {
    data: productData,
    isLoading: isProductLoading,
    error: productError,
  } = useQuery(
    [
      "productData",
      timeRange,
      startDate,
      endDate,
      postingAccount,
      zone,
      state,
      page,
      limit,
      sortField,
      sortDirection,
      selectedProducts.join("|"),
    ],
    () => {
      // Only send valid backend sortField values
      const validBackendSortFields = ["product_name", "total_net_amount", "total_qty"];
      const backendSortField = validBackendSortFields.includes(sortField) 
        ? sortField 
        : (timeRange === "month" ? "total_net_amount" : "total_net_amount");
      
      // Format dates: month uses en-CA, day-wise uses en-CA (YYYY-MM-DD)
      const formattedStartDate = timeRange === "month" ? startDate.toLocaleDateString("en-CA") : startDate.toLocaleDateString("en-CA");
      const formattedEndDate = timeRange === "month" ? endDate.toLocaleDateString("en-CA") : endDate.toLocaleDateString("en-CA");

      const stateFilter = state && String(state).trim();
      const hasSelectedProducts = selectedProducts.length > 0;
      const selectedSkuList = selectedProducts
        .map((name) => selectedProductSkus[name])
        .filter((sku) => sku && String(sku).trim())
        .map((sku) => String(sku).trim());
      const params = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        page: hasSelectedProducts ? 1 : page,
        limit,
        sortField: backendSortField,
        sortValue: sortDirection === "asc" ? "ASC" : "DESC",
        ...(postingAccount && { postingAccount }),
        ...(stateFilter && { state: stateFilter }),
        ...(zone && { zone }),
      };
      if (hasSelectedProducts && selectedSkuList.length > 0) {
        params.productSkus = selectedSkuList.join(",");
      } else if (hasSelectedProducts && selectedProducts.length === 1) {
        const selectedName = selectedProducts[0];
        params.productName = selectedName;
      }
      return timeRange === "month"
        ? getProductMonthWiseData(params)
        : getProductDayWiseData(params);
    },
    {
      onError: (error) => {
        console.log("Product Data Error:", error);
        toast.error(error.message || "Failed to load product data");
      },
    }
  );

  // Extract unique product names from the already fetched product data
  const availableProducts = useMemo(() => {
    if (!productData?.data) return [];
    const products = productData.data
      .filter((product) => {
        // Filter out totals and grand totals
        const productName = product.product_name?.toLowerCase() || "";
        return !productName.includes("total") && !productName.includes("grand");
      })
      .map((product) => ({
        productName: product.product_name,
        sku: product.sku || product.baseSku || "",
      }));
    
    // Remove duplicates and sort
    const uniqueProducts = Array.from(
      new Map(products.map((p) => [p.productName, p])).values()
    ).sort((a, b) => a.productName.localeCompare(b.productName));
    
    return uniqueProducts;
  }, [productData]);

  const {
    data: searchedProductsData,
    isLoading: isProductSearchLoading,
  } = useQuery(
    ["product-search-options", debouncedProductSearchTerm, postingAccount],
    () =>
      getAllProducts({
        page: 1,
        limit: 100,
        searchKey: debouncedProductSearchTerm,
        postingAccount,
      }),
    {
      enabled: isProductDropdownOpen && debouncedProductSearchTerm.length > 0,
      keepPreviousData: true,
      staleTime: 30 * 1000,
      onError: () => {
        toast.error("Failed to search products");
      },
    }
  );

  const dropdownProducts = useMemo(() => {
    if (debouncedProductSearchTerm.length === 0) return availableProducts;
    const rows = searchedProductsData?.data || [];
    const mapped = rows
      .map((product) => ({
        productName: (product.productName || "").toString().trim(),
        sku: (product.sku || product.baseSku || product.channelSku || "")
          .toString()
          .trim(),
      }))
      .filter((p) => p.productName.length > 0);
    return Array.from(new Map(mapped.map((p) => [p.productName, p])).values()).sort(
      (a, b) => a.productName.localeCompare(b.productName)
    );
  }, [availableProducts, debouncedProductSearchTerm, searchedProductsData]);

  const selectedSkuSet = useMemo(
    () =>
      new Set(
        selectedProducts
          .map((name) => selectedProductSkus[name])
          .filter((sku) => sku && String(sku).trim())
          .map((sku) => String(sku).trim())
      ),
    [selectedProducts, selectedProductSkus]
  );

  const dayRangeKeys = useMemo(() => {
    if (timeRange === "month") return [];
    const keys = [];
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    while (current <= end) {
      keys.push(current.toLocaleDateString("en-CA"));
      current.setDate(current.getDate() + 1);
    }
    return keys;
  }, [timeRange, startDate, endDate]);

  const createZeroProductRow = (name, sku) => {
    if (timeRange === "month") {
      return {
        product_name: name,
        sku: sku || "",
        baseSku: sku || null,
        Jan: { net_amount: 0, qty: 0 },
        Feb: { net_amount: 0, qty: 0 },
        Mar: { net_amount: 0, qty: 0 },
        Apr: { net_amount: 0, qty: 0 },
        May: { net_amount: 0, qty: 0 },
        Jun: { net_amount: 0, qty: 0 },
        Jul: { net_amount: 0, qty: 0 },
        Aug: { net_amount: 0, qty: 0 },
        Sep: { net_amount: 0, qty: 0 },
        Oct: { net_amount: 0, qty: 0 },
        Nov: { net_amount: 0, qty: 0 },
        Dec: { net_amount: 0, qty: 0 },
        total: { net_amount: 0, qty: 0 },
      };
    }

    const daily_data = {};
    dayRangeKeys.forEach((d) => {
      daily_data[d] = { net_amount: 0, qty: 0 };
    });
    return {
      product_name: name,
      sku: sku || "",
      baseSku: sku || null,
      daily_data,
      total_net_amount: 0,
      total_qty: 0,
    };
  };

  const ensureSelectedProductsPresent = (rows) => {
    if (selectedProducts.length === 0) return rows;
    const next = [...rows];
    selectedProducts.forEach((name) => {
      const selectedSku = (selectedProductSkus[name] || "").toString().trim();
      const exists = rows.some((row) => {
        const rowSku = (row.sku || row.baseSku || "").toString().trim();
        if (selectedSku) return rowSku === selectedSku;
        return (row.product_name || "").toString() === name;
      });
      if (!exists) {
        next.push(createZeroProductRow(name, selectedSku));
      }
    });
    return next;
  };

  const isSelectedMatch = (product) => {
    if (selectedProducts.length === 0) return true;
    const name = (product.product_name || "").toString();
    const sku = (product.sku || product.baseSku || "").toString().trim();
    if (selectedSkuSet.size > 0) {
      if (sku && selectedSkuSet.has(sku)) return true;
    }
    return selectedProducts.includes(name);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setIsProductDropdownOpen(false);
      }
    };

    if (isProductDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProductDropdownOpen]);

  // Separate query for top products (for bar chart)
  const {
    data: topProductsData,
    isLoading: isTopProductsLoading,
  } = useQuery(
    ["topProductsData", timeRange, startDate, endDate, postingAccount, zone, state],
    () => {
      const formattedStartDate = startDate.toLocaleDateString("en-CA");
      const formattedEndDate = endDate.toLocaleDateString("en-CA");

      const stateFilter = state && String(state).trim();
      const params = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        page: 1,
        limit: 15, // Fetch top 15 products for chart
        sortField: "total_net_amount", // Always sort by amount
        sortValue: "DESC", // Always descending
        ...(postingAccount && { postingAccount }),
        ...(stateFilter && { state: stateFilter }),
        ...(zone && { zone }),
      };
      return timeRange === "month"
        ? getProductMonthWiseData(params)
        : getProductDayWiseData(params);
    },
    {
      onError: (error) => {
        console.log("Top Products Data Error:", error);
        // Don't show error toast for top products, just log it
      },
      refetchOnWindowFocus: false,
    }
  );


  const formatCell = (data) => {
    if (!data) return showQuantity ? "0" : "₹0";
    return showQuantity
      ? `${data.qty}`
      : `${formatAmount(data.net_amount ?? 0)}`;
  };

  // Export to CSV function
  const handleExportToCSV = async () => {
    setIsExporting(true);
    try {
      const stateFilter = state && String(state).trim();
      const params = new URLSearchParams(
        timeRange === "month"
          ? {
            sortField: "total_qty",
            sortValue: "DESC",
            startMonth: startDate.toLocaleDateString("en-CA"),
            endMonth: endDate.toLocaleDateString("en-CA"),
            exportToCsv: "true",
            page: 1,
            limit: 5000,
            qtyReq: showQuantity,
            ...(postingAccount && { postingAccount }),
            ...(stateFilter && { state: stateFilter }),
            ...(zone && { zone }),
          }
          : {
            sortField: "total_qty",
            sortValue: "DESC",
            startDate: startDate.toLocaleDateString("en-CA"),
            endDate: endDate.toLocaleDateString("en-CA"),
            exportToCsv: "true",
            page: 1,
            limit: 5000,
            qtyReq: showQuantity,
            ...(postingAccount && { postingAccount }),
            ...(stateFilter && { state: stateFilter }),
            ...(zone && { zone }),
          }
      );

      const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://digitalapi.palsonsderma.com';
      const response = await fetch(
        `${baseURL}/data/order/${timeRange === "month"
          ? "product-wise-month-wise-data"
          : "product-wise-day-wise-data"
        }?${params.toString()}`,
        {
          method: "GET",
          credentials: "include", // only if you're using cookies for auth
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      // Generate dynamic filename based on current filters
      const filename = `product-analytics-${startDate.toLocaleDateString(
        "en-CA"
      )}-to-${endDate.toLocaleDateString("en-CA")}${postingAccount ? `-${postingAccount}` : ""
        }${state ? `-${state}` : ""}${zone ? `-${zone}` : ""}.csv`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url); // cleanup
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download CSV");
    } finally {
      setIsExporting(false);
    }
  };

  // Prepare data for charts
  const prepareChartData = () => {
    // Use productData when products are selected, otherwise use topProductsData
    const sourceData = selectedProducts.length > 0 ? productData : topProductsData;
    if (!sourceData?.data) return [];
    const sourceRows = ensureSelectedProductsPresent(sourceData.data);

    // Filter out products with no data and exclude totals
    let productChartData = sourceRows
      .filter((product) => {
        // Filter out totals and grand totals
        const productName = product.product_name?.toLowerCase() || "";
        if (productName.includes("total") || productName.includes("grand")) {
          return false;
        }

        // Filter by selected products if any are selected
        if (!isSelectedMatch(product)) {
          return false;
        }

        let amount = 0;
        if (timeRange === "month") {
          amount = showQuantity
            ? product.total?.qty || 0
            : product.total?.net_amount || 0;
        } else {
          amount = showQuantity
            ? product.total_qty || 0
            : product.total_net_amount || 0;
        }
        // When selected products are explicitly chosen, keep zero rows visible too.
        if (selectedProducts.length > 0 && isSelectedMatch(product)) return true;
        return amount > 0;
      })
      .map((product) => {
        let amount = 0;
        if (timeRange === "month") {
          amount = showQuantity
            ? product.total?.qty || 0
            : product.total?.net_amount || 0;
        } else {
          amount = showQuantity
            ? product.total_qty || 0
            : product.total_net_amount || 0;
        }

        // Create a shortened name for the chart display
        const shortName =
          product.product_name.length > 20
            ? product.product_name.substring(0, 20) + "..."
            : product.product_name;

        return {
          name: shortName,
          fullName: product.product_name, // Keep full name for tooltip
          value: amount,
          formattedValue: showQuantity
            ? amount.toString()
            : formatAmount(amount),
        };
      })
      .sort((a, b) => b.value - a.value); // Sort by value descending

    return productChartData;
  };

  const renderTopProductsBarChart = () => {
    const barData = prepareChartData();
    const displayData = showAllProducts ? barData : barData.slice(0, 10);
    const chartHeight = showAllProducts
      ? Math.max(400, displayData.length * 32)
      : 400;

    const renderHorizontalBarLabel = ({ x, y, width, height, value }) => {
      if (value == null) return null;
      const label = showQuantity ? `${value} units` : formatAmount(value);
      const centerY = y + (height || 0) / 2;

      // Check if bar is too small to show label above (less than 30px width)
      const isBarTooSmall = (width || 0) < 30;

      let labelX, labelY;

      if (isBarTooSmall) {
        // Position label to the right of the bar when bar is too small
        labelX = (x || 0) + (width || 0) + (showQuantity ? 45 : 30);
        labelY = centerY;
      } else {
        // Position label inside the bar when bar is large enough
        labelX = (x || 0) + (width || 0) / 2; // Center of the bar
        labelY = centerY; // Inside the bar
      }

      return (
        <text
          x={labelX}
          y={labelY}
          fill={isBarTooSmall ? "#6b7280" : "white"}
          fontSize={12}
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {label}
        </text>
      );
    };

    // Show loading state
    if (isTopProductsLoading) {
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Top Products Performance
          </h3>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading top products...</span>
          </div>
        </div>
      );
    }

    // Don't render if no data
    if (!barData || barData.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Top Products Performance 
          </h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            No data available for the selected time range
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">
          Top Products Performance 
        </h3>

        <div className="relative overflow-visible">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={displayData}
              layout="vertical"
              margin={{ top: 10, right: 100, left: 0, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" />
              <YAxis
                dataKey="name"
                type="category"
                width={250}
                tick={{ fontSize: 11, fill: "#374151" }}
                tickFormatter={(value) => value.toUpperCase()}
                interval={0}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value, name, props) => [
                  showQuantity ? `${value} units` : formatAmount(value),
                  showQuantity ? "Quantity" : "Amount",
                ]}
                labelFormatter={(value) => {
                  // Find the full name from the data
                  const dataPoint = displayData.find(
                    (item) => item.name === value
                  );
                  return dataPoint?.fullName || value;
                }}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  maxWidth: 280,
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                wrapperStyle={{ maxWidth: 280 }}
                labelStyle={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                {displayData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`hsl(${200 + index * 15}, 70%, ${50 + index * 3}%)`}
                  />
                ))}
                <LabelList content={renderHorizontalBarLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {barData.length > 10 && (
          <div className="flex justify-center mt-2">
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setShowAllProducts((prev) => !prev)}
            >
              {showAllProducts ? "View Less" : `View More (${barData.length})`}
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleSort = (field) => {
    // Valid backend sort fields
    const validBackendSortFields = ["product_name", "total_net_amount", "total_qty"];
    
    // Map frontend sort field to backend sort field
    let backendSortField = field;
    if (field === "product_name") {
      backendSortField = "product_name";
    } else if (field === "total") {
      backendSortField = timeRange === "month" ? "total_net_amount" : "total_net_amount";
    } else {
      // For month/date columns (like "Dec", "Jan", etc.), backend doesn't support sorting
      // So we'll keep client-side sorting for these - don't send to backend
      if (sortField === field) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortField(field); // Store for client-side sorting
        setSortDirection("asc");
      }
      return; // Don't reset page for client-side sorting, don't send to backend
    }

    // Only update backend sortField if it's a valid field
    if (validBackendSortFields.includes(backendSortField)) {
      if (sortField === backendSortField) {
        // Toggle direction if same field
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        // New field, start with ascending
        setSortField(backendSortField);
        setSortDirection("asc");
      }
      setPage(1); // Reset to first page when sorting changes
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="inline-block ml-1 text-gray-400">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 2L9 6H3L6 2Z" />
            <path d="M6 10L3 6H9L6 10Z" opacity="0.4" />
          </svg>
        </span>
      );
    }

    if (sortDirection === "asc") {
      return (
        <span className="inline-block ml-1 text-blue-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 2L9 6H3L6 2Z" />
          </svg>
        </span>
      );
    } else {
      return (
        <span className="inline-block ml-1 text-blue-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 10L3 6H9L6 10Z" />
          </svg>
        </span>
      );
    }
  };

  const sortData = (data, field, direction) => {
    if (!field) return data;

    // Separate grand total rows from regular data
    const grandTotalRows = data.filter((item) => {
      const name = item.product_name?.toLowerCase() || "";
      return name.includes("total") || name.includes("grand");
    });

    const regularData = data.filter((item) => {
      const name = item.product_name?.toLowerCase() || "";
      return !(name.includes("total") || name.includes("grand"));
    });

    // Sort only the regular data
    const sortedRegularData = [...regularData].sort((a, b) => {
      let aVal, bVal;

      if (field === "product_name") {
        aVal = a.product_name?.toLowerCase() || "";
        bVal = b.product_name?.toLowerCase() || "";
      } else if (field === "total") {
        if (timeRange === "month") {
          aVal = showQuantity ? a.total?.qty || 0 : a.total?.net_amount || 0;
          bVal = showQuantity ? b.total?.qty || 0 : b.total?.net_amount || 0;
        } else {
          aVal = showQuantity ? a.total_qty || 0 : a.total_net_amount || 0;
          bVal = showQuantity ? b.total_qty || 0 : b.total_net_amount || 0;
        }
      } else {
        // For month/date columns
        if (timeRange === "month") {
          aVal = showQuantity ? a[field]?.qty || 0 : a[field]?.net_amount || 0;
          bVal = showQuantity ? b[field]?.qty || 0 : b[field]?.net_amount || 0;
        } else {
          // For day columns
          const aTimeData = a.daily_data;
          const bTimeData = b.daily_data;
          aVal = showQuantity
            ? aTimeData?.[field]?.qty || 0
            : aTimeData?.[field]?.net_amount || 0;
          bVal = showQuantity
            ? bTimeData?.[field]?.qty || 0
            : bTimeData?.[field]?.net_amount || 0;
        }
      }

      if (direction === "asc") {
        if (typeof aVal === "string") return aVal.localeCompare(bVal);
        return aVal - bVal;
      } else {
        if (typeof aVal === "string") return bVal.localeCompare(aVal);
        return bVal - aVal;
      }
    });

    // Combine sorted regular data with grand total rows at the end
    return [...sortedRegularData, ...grandTotalRows];
  };

  const renderTable = () => {
    if (!productData?.data) return null;

    // Backend sorts and paginates for product_name and total fields
    // For month/date columns, apply client-side sorting if needed
    let products = ensureSelectedProductsPresent(productData.data);
    
    // Filter by selected products if any are selected
    if (selectedProducts.length > 0) {
      products = products.filter((product) => isSelectedMatch(product));
    }
    
    if (sortField && (sortField !== "product_name" && sortField !== "total_net_amount" && sortField !== "total_qty")) {
      products = sortData(products, sortField, sortDirection);
    }

    if (timeRange === "month") {
      // Generate array of months between startDate and endDate (inclusive)
      const monthList = [];
      let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      while (current <= end) {
        monthList.push({
          key: `${current.getFullYear()}-${current.getMonth() + 1}`,
          label: current.toLocaleString("default", { month: "short" }),
          year: current.getFullYear(),
          month: current.getMonth(),
        });
        current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
      }
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none w-[220px] min-w-[220px] max-w-[220px]"
                  style={{ width: 220 }}
                  onClick={() => handleSort("product_name")}
                >
                  <div className="flex items-center">
                    Product Name
                    {getSortIcon("product_name")}
                  </div>
                </th>
                {monthList.map((m) => (
                  <th
                    key={m.key}
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort(m.label)}
                  >
                    <div className="flex items-center">
                      {m.label} {m.year}
                      {getSortIcon(m.label)}
                    </div>
                  </th>
                ))}
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("total")}
                >
                  <div className="flex items-center">
                    Total
                    {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => {
                const isGrandTotal =
                  product.product_name?.toLowerCase().includes("total") ||
                  product.product_name?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={isGrandTotal ? "font-bold" : ""}
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                  >
                    <td
                      className={`capitalize transition-colors duration-150 w-[320px] min-w-[320px] max-w-[320px] ${!isGrandTotal
                        ? "cursor-pointer hover:text-blue-700"
                        : ""
                        }`}
                      style={{ cursor: isGrandTotal ? "default" : "pointer", width: 220 }}
                    >
                      <div className="flex flex-col min-w-0 overflow-hidden">
                        <span
                          className="break-words line-clamp-4"
                          title={product.product_name}
                          onClick={
                            !isGrandTotal
                              ? () =>
                                navigate(
                                  `/analytics/product-detail/${encodeURIComponent(
                                    product.sku
                                  )}`,
                                  {
                                    state: {
                                      productName: product.product_name,
                                      useSku: true,
                                      startDate:
                                        startDate.toLocaleDateString("en-CA"),
                                      endDate:
                                        endDate.toLocaleDateString("en-CA"),
                                      fromPage: "/analytics/product",
                                      timeRange: timeRange,
                                      postingAccount: postingAccount,
                                      state: state,
                                      zone: zone,
                                      showQuantity: showQuantity,
                                      showAllProducts: showAllProducts,
                                    },
                                  }
                                )
                              : undefined
                          }
                        >
                          {product.product_name}
                        </span>
                        {product.baseSku && (
                          <span className="text-xs text-green-500 mt-0.5">
                            Base SKU: {product.baseSku}
                          </span>
                        )}
                      </div>
                    </td>
                    {monthList.map((m) => {
                      // Assuming product[month] is keyed by month short name (e.g., 'Jan'), but you may need to adjust this if the data is keyed differently
                      const key = m.label;
                      return <td key={m.key}>{formatCell(product[key])}</td>;
                    })}
                    <td>{formatCell(product.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      // For daily view
      const dates = Object.keys(products[0]?.daily_data || {})
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => {
          const d = new Date(date);
          return {
            fullDate: date,
            displayDate: `${d.getDate()}-${d.toLocaleString("default", {
              month: "short",
            })}`,
          };
        });

      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none  w-[320px] min-w-[320px] max-w-[320px]"
                  style={{ width: 220 }}
                  onClick={() => handleSort("product_name")}
                >
                  <div className="flex items-center">
                    Product Name
                    {getSortIcon("product_name")}
                  </div>
                </th>
                {dates.map(({ fullDate, displayDate }) => (
                  <th
                    key={fullDate}
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort(fullDate)}
                  >
                    <div className="flex items-center">
                      {displayDate} {fullDate.split("-")[0]}
                      {getSortIcon(fullDate)}
                    </div>
                  </th>
                ))}
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("total")}
                >
                  <div className="flex items-center">
                    Total
                    {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => {
                const isGrandTotal =
                  product.product_name?.toLowerCase().includes("total") ||
                  product.product_name?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={isGrandTotal ? "font-bold" : ""}
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                  >
                    <td
                      className={` capitalize transition-colors duration-150 w-[220px] min-w-[220px] max-w-[220px] ${!isGrandTotal
                        ? "cursor-pointer hover:text-blue-700"
                        : ""
                        }`}
                      style={{ cursor: isGrandTotal ? "default" : "pointer", width: 220 }}
                    >
                      <div className="flex flex-col min-w-0 overflow-hidden">
                        <span
                          className="break-words line-clamp-4"
                          title={product.product_name}
                          onClick={
                            !isGrandTotal
                              ? () =>
                                navigate(
                                  `/analytics/product-detail/${encodeURIComponent(
                                    product.sku
                                  )}`,
                                  {
                                    state: {
                                      productName: product.product_name,
                                      useSku: true,
                                      startDate:
                                        startDate.toLocaleDateString("en-CA"),
                                      endDate:
                                        endDate.toLocaleDateString("en-CA"),
                                      fromPage: "/analytics/product",
                                      timeRange: timeRange,
                                      postingAccount: postingAccount,
                                      state: state,
                                      zone: zone,
                                      showQuantity: showQuantity,
                                      showAllProducts: showAllProducts,
                                    },
                                  }
                                )
                              : undefined
                          }
                        >
                          {product.product_name}
                        </span>
                        {product.baseSku && (
                          <span className="text-xs text-green-500 mt-0.5">
                            Base SKU: {product.baseSku}
                          </span>
                        )}
                      </div>
                    </td>
                    {dates.map(({ fullDate }) => (
                      <td key={`${product.product_name}-${fullDate}`}>
                        {formatCell(product.daily_data?.[fullDate])}
                      </td>
                    ))}
                    <td>
                      {formatCell({
                        net_amount: product.total_net_amount,
                        qty: product.total_qty,
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  };

  if (isProductLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <h1 className="text-2xl font-bold">Product Analytics</h1>
        <div className="flex flex-wrap gap-4 items-center w-full lg:w-auto">
          <select
            className="select select-bordered w-full lg:w-40"
            value={postingAccount}
            onChange={(e) => setPostingAccount(e.target.value)}
          >
            <option value="">All Platforms</option>
            <option value="amazon">Amazon</option>
            <option value="flipkart">Flipkart</option>
            <option value="shopify">Shopify</option>
            <option value="tata 1mg">Tata 1mg</option>
            <option value="palsons derma pvt ltd">Palsons Derma Pvt Ltd</option>
          </select>

          <select
            className="select select-bordered w-full lg:w-40"
            value={zone}
            onChange={(e) => setZone(e.target.value)}
          >
            <option value="">All Zones</option>
            <option value="north">North</option>
            <option value="south">South</option>
            <option value="east">East</option>
            <option value="west">West</option>
            <option value="central">Central</option>
            <option value="north east">North East</option>
          </select>

          {zone && (
            <select
              className="select select-bordered w-full lg:w-40"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="">All States</option>
              {statesForZone.map((item) => (
                <option key={item.state} value={item.state}>
                  {item.state}
                </option>
              ))}
            </select>
          )}

          {/* Multi-select Product Dropdown */}
          <div className="relative w-full lg:w-64" ref={productDropdownRef}>
            <div
              className="input input-bordered w-full cursor-pointer flex items-center justify-between"
              onClick={() => setIsProductDropdownOpen(!isProductDropdownOpen)}
            >
              <div className="flex-1 flex flex-wrap gap-1">
                {selectedProducts.length === 0 ? (
                  <span className="text-gray-400">Select Products</span>
                ) : (
                  selectedProducts.slice(0, 2).map((productName) => (
                    <span
                      key={productName}
                      className="badge badge-primary badge-sm"
                    >
                      {productName.length > 20
                        ? `${productName.substring(0, 20)}...`
                        : productName}
                    </span>
                  ))
                )}
                {selectedProducts.length > 2 && (
                  <span className="badge badge-primary badge-sm">
                    +{selectedProducts.length - 2} more
                  </span>
                )}
              </div>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isProductDropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {isProductDropdownOpen && (
              <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-hidden">
                {/* Search Input */}
                <div className="p-2 border-b border-gray-200">
                  <input
                    type="text"
                    placeholder="Search products..."
                    className="input input-bordered input-sm w-full"
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Product List */}
                <div className="max-h-64 overflow-y-auto">
                  {isProductSearchLoading || (isProductLoading && !productSearchTerm.trim()) ? (
                    <div className="p-4 text-center text-gray-400 text-sm">
                      Loading products...
                    </div>
                  ) : (() => {
                    if (dropdownProducts.length === 0) {
                      return (
                        <div className="p-4 text-center text-gray-400 text-sm">
                          {productSearchTerm ? "No products found" : "No products available"}
                        </div>
                      );
                    }

                    return dropdownProducts.map((product) => {
                      const isSelected = selectedProducts.includes(
                        product.productName
                      );
                      return (
                        <div
                          key={product.productName}
                          className="p-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedProducts(
                                selectedProducts.filter(
                                  (name) => name !== product.productName
                                )
                              );
                              setSelectedProductSkus((prev) => {
                                const next = { ...prev };
                                delete next[product.productName];
                                return next;
                              });
                            } else {
                              setSelectedProducts([
                                ...selectedProducts,
                                product.productName,
                              ]);
                              if (product.sku) {
                                setSelectedProductSkus((prev) => ({
                                  ...prev,
                                  [product.productName]: product.sku,
                                }));
                              }
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="checkbox checkbox-primary checkbox-sm"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="flex-1 text-sm">
                            {product.productName}
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Selected Products Summary */}
                {selectedProducts.length > 0 && (
                  <div className="p-2 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      {selectedProducts.length} selected
                    </span>
                    <button
                      className="btn btn-xs btn-ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProducts([]);
                        setSelectedProductSkus({});
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 items-center w-full lg:w-auto">
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              className="text-sm input input-bordered w-full lg:w-40"
              dateFormat={timeRange === "month" ? "yyyy-MM" : "yyyy-MM-dd"}
              showMonthYearPicker={timeRange === "month"}
              showFullMonthYearPicker={timeRange === "month"}
              minDate={
                timeRange === "month"
                  ? new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1) // 1 year for monthly view
                  : new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000) // 1 year for daily view
              }
              maxDate={endDate}
            />
            <DatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              className="text-sm input input-bordered w-full lg:w-40"
              dateFormat={timeRange === "month" ? "yyyy-MM" : "yyyy-MM-dd"}
              showMonthYearPicker={timeRange === "month"}
              showFullMonthYearPicker={timeRange === "month"}
              maxDate={new Date()}
            />
          </div>

          <div className="btn-group">
            <button
              className={`btn btn-sm ${timeRange === "day" ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setTimeRange("day")}
            >
              Daily
            </button>
            <button
              className={`btn btn-sm ${timeRange === "month" ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setTimeRange("month")}
            >
              Monthly
            </button>
          </div>

          <div className="form-control">
            <label className="label cursor-pointer">
              <span className="label-text mr-2">Units Sold</span>
              <input
                type="checkbox"
                className="toggle toggle-primary"
                checked={showQuantity}
                onChange={(e) => setShowQuantity(e.target.checked)}
              />
            </label>
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={handleExportToCSV}
            disabled={isProductLoading || isExporting}
          >
            {isProductLoading || isExporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              "Export CSV"
            )}
          </button>
        </div>
      </div>

      {renderTopProductsBarChart()}

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            Product Performance Details
          </h3>
          <div className=" overflow-x-auto">{renderTable()}</div>
          
          {/* Pagination */}
          {productData?.count > 0 && (
            <Pagination
              page={page}
              onPageChange={setPage}
              limit={limit}
              totalCount={productData.count}
              hasNext={productData.hasNext}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductAnalytics;
